/**
 * User ID Hashing Utility
 * 
 * Provides privacy-compliant hashing of user IDs for error tracking.
 * Uses Web Crypto API for SHA-256 hashing.
 */

/**
 * Hash a user ID using SHA-256
 * 
 * @param userId - The user ID to hash
 * @returns Promise<string> - The hashed user ID (hex string)
 */
export async function hashUserId(userId: string): Promise<string> {
  // Convert userId to Uint8Array
  const encoder = new TextEncoder();
  const data = encoder.encode(userId);
  
  // Hash using Web Crypto API
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  
  // Convert to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex;
}

/**
 * Synchronous hash function (for cases where async is not possible)
 * Uses a simple hash function - less secure but synchronous
 * 
 * @param userId - The user ID to hash
 * @returns string - The hashed user ID
 */
export function hashUserIdSync(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Convert to positive hex string
  return Math.abs(hash).toString(16).padStart(8, '0');
}
